import { X } from "lucide-react";
import React from "react";

import toast from "../toast";

interface Addon {
  name: string;
  price: string;
}

interface ItemEditModalProps {
  categories: { id: string; name: string }[];
  item: any;
  onClose: () => void;
  onSubmit: (data: any) => void;
  open: boolean;
}

interface Variant {
  name: string;
  price: string;
  stock: string;
}

// Helper to build full image URL from API response
const getImageUrl = (imgPath: File | string) => {
  if (!imgPath) return "";
  if (imgPath instanceof File) {
    return URL.createObjectURL(imgPath);
  }
  if (typeof imgPath === "string") {
    const BASE_URL = import.meta.env.VITE_API_STORAGE_URL || "";
    if (imgPath.startsWith("http")) return imgPath;
    return (
      BASE_URL.replace(/\/$/, "") + "/storage/" + imgPath.replace(/^\//, "")
    );
  }
  return "";
};

const ItemEditModal: React.FC<ItemEditModalProps> = ({
  categories,
  item,
  onClose,
  onSubmit,
  open,
}) => {
  const [step, setStep] = React.useState(1);
  const [form, setForm] = React.useState({
    addons: Array.isArray(item?.addons)
      ? item.addons
      : typeof item?.addons === "object" && item?.addons !== null
        ? Object.values(item.addons)
        : [],
    availability: item?.availability || "all",
    category: item?.category || categories[0] || "",
    description: item?.description || "",
    featured: item?.featured ?? false,
    images: item?.images || (item?.image ? [item.image] : []),
    isAvailable: item?.isAvailable ?? true,
    name: item?.name || "",
    popular: item?.popular ?? false,
    price: item?.price?.toString() || "",
    promoPrice: item?.promoPrice?.toString() || "",
    sku: item?.sku || "",
    slug: item?.slug || "",
    variants: Array.isArray(item?.variants)
      ? item.variants
      : typeof item?.variants === "object" && item?.variants !== null
        ? Object.values(item.variants)
        : [],
  });

  React.useEffect(() => {
    if (item) {
      setForm({
        addons: Array.isArray(item.addons)
          ? item.addons
          : typeof item.addons === "object" && item.addons !== null
            ? Object.values(item.addons)
            : [],
        availability: item.availability || "all",
        category: item.category || categories[0] || "",
        description: item.description || "",
        featured: item.featured ?? false,
        images: item.images || (item.image ? [item.image] : []),
        isAvailable: item.isAvailable ?? true,
        name: item.name || "",
        popular: item.popular ?? false,
        price: item.price?.toString() || "",
        promoPrice: item.promoPrice?.toString() || "",
        sku: item.sku || "",
        slug: item.slug || "",
        variants: Array.isArray(item.variants)
          ? item.variants
          : typeof item.variants === "object" && item.variants !== null
            ? Object.values(item.variants)
            : [],
      });
    }
  }, [item, categories]);

  if (!open || !item) return null;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, type, value } = e.target;
    const val =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setForm((f) => ({
      ...f,
      [name]: val,
    }));
    if (name === "name") {
      const slug = value.toLowerCase().replace(/\s+/g, "-");
      const sku =
        form.sku ||
        "SKU-" + Math.random().toString(36).substring(2, 8).toUpperCase();
      setForm((f) => ({
        ...f,
        sku,
        slug,
      }));
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({
      ...f,
      images: [file], // Only allow one image, store as File
    }));
  };

  // Variants
  const addVariant = () => {
    setForm((f) => ({
      ...f,
      variants: [...f.variants, { name: "", price: "", stock: "" }],
    }));
  };
  const updateVariant = (i: number, key: string, value: string) => {
    const updated = [...form.variants];
    (updated[i] as any)[key] = value;
    setForm((f) => ({ ...f, variants: updated }));
  };
  const removeVariant = (i: number) =>
    setForm((f) => ({
      ...f,
      variants: f.variants.filter((_: Variant, idx: number) => idx !== i),
    }));

  // Add-ons
  const addAddon = () => {
    setForm((f) => ({
      ...f,
      addons: [...f.addons, { name: "", price: "" }],
    }));
  };
  const updateAddon = (i: number, key: string, value: string) => {
    const updated = [...form.addons];
    (updated[i] as any)[key] = value;
    setForm((f) => ({ ...f, addons: updated }));
  };
  const removeAddon = (i: number) =>
    setForm((f) => ({
      ...f,
      addons: f.addons.filter((_: Addon, idx: number) => idx !== i),
    }));

  // Validation logic for each step
  const isStepValid = () => {
    if (step === 1) {
      return (
        form.name.trim() && form.category.trim() && form.description.trim()
      );
    }
    if (step === 2) {
      return form.price.trim() && !isNaN(Number(form.price));
    }
    if (step === 3) {
      return true;
    }
    if (step === 4) {
      return true;
    }
    if (step === 5) {
      return form.images.length > 0;
    }
    return true;
  };

  // Submit
  const submit = async () => {
    // Build data object for update
    const data: any = {
      availability: form.availability,
      category: form.category,
      description: form.description,
      featured: form.featured,
      isAvailable: form.isAvailable,
      name: form.name,
      popular: form.popular,
      price: Number(form.price),
      sku: form.sku,
      slug: form.slug,
    };

    if (form.promoPrice) {
      data.promoPrice = Number(form.promoPrice);
    }

    // Add variants
    if (form.variants && form.variants.length > 0) {
      data.variants = form.variants.map((v: Variant) => ({
        name: v.name,
        price: Number(v.price),
        stock: Number(v.stock),
      }));
    }

    // Add addons
    if (form.addons && form.addons.length > 0) {
      data.addons = form.addons.map((a: Addon) => ({
        name: a.name,
        price: Number(a.price),
      }));
    }

    // Add image as file if present
    let fd: FormData | undefined;
    if (form.images[0] && form.images[0] instanceof File) {
      fd = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        fd!.append(key, value as any);
      });
      fd.append("image", form.images[0]);
    }
    try {
      const MenuService = (await import("@/services/MenuService")).default;
      if (fd) {
        await MenuService.updateMenuItem(item.id, fd as any);
      } else {
        await MenuService.updateMenuItem(item.id, data);
      }
      toast.success("Menu item updated successfully!");
      onSubmit(form); // Notify parent to refresh list
      setTimeout(() => {
        onClose(); // Close modal after short delay so toast is visible
      }, 1200);
    } catch (err: any) {
      toast.error(
        "Error: " +
          (err?.response?.data?.message || "Failed to update menu item."),
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative grid w-full max-w-4xl grid-cols-1 gap-4 rounded-xl bg-white p-6 shadow-lg dark:bg-gray-900 md:grid-cols-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full bg-transparent p-2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-200"
          onClick={onClose}
          type="button"
        >
          <X className="h-6 w-6" />
        </button>
        {/* --- LEFT SIDE FORM --- */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Edit Menu Item — Step {step}
          </h2>
          {/* STEP 1 — BASIC INFO */}
          {step === 1 && (
            <div className="space-y-3">
              <input
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                name="name"
                onChange={handleChange}
                placeholder="Item Name"
                value={form.name}
              />
              <select
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                name="category"
                onChange={handleChange}
                value={form.category}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              <textarea
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                name="description"
                onChange={handleChange}
                placeholder="Description"
                value={form.description}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Slug: {form.slug}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                SKU: {form.sku}
              </p>
            </div>
          )}
          {/* STEP 2 — PRICE & AVAILABILITY */}
          {step === 2 && (
            <div className="space-y-3">
              <input
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                name="price"
                onChange={handleChange}
                placeholder="Base Price"
                type="number"
                value={form.price}
              />
              <input
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                name="promoPrice"
                onChange={handleChange}
                placeholder="Promo Price (optional)"
                type="number"
                value={form.promoPrice}
              />
              <select
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                name="availability"
                onChange={handleChange}
                value={form.availability}
              >
                <option value="all">Available All Day</option>
                <option value="breakfast">Breakfast Only</option>
                <option value="lunch">Lunch Only</option>
                <option value="dinner">Dinner Only</option>
              </select>
              <label className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                <input
                  checked={form.isAvailable}
                  className="border-gray-300 bg-gray-100 accent-blue-600 dark:border-gray-700 dark:bg-gray-800 dark:accent-blue-400"
                  name="isAvailable"
                  onChange={handleChange}
                  type="checkbox"
                />
                Available
              </label>
              <label className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                <input
                  checked={form.featured}
                  className="border-gray-300 bg-gray-100 accent-blue-600 dark:border-gray-700 dark:bg-gray-800 dark:accent-blue-400"
                  name="featured"
                  onChange={handleChange}
                  type="checkbox"
                />
                Featured
              </label>
              <label className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                <input
                  checked={form.popular}
                  className="border-gray-300 bg-gray-100 accent-blue-600 dark:border-gray-700 dark:bg-gray-800 dark:accent-blue-400"
                  name="popular"
                  onChange={handleChange}
                  type="checkbox"
                />
                Popular
              </label>
            </div>
          )}
          {/* STEP 3 — VARIANTS */}
          {step === 3 && (
            <div className="space-y-3">
              <button
                className="rounded bg-blue-600 px-3 py-2 text-white dark:bg-blue-700"
                onClick={addVariant}
                type="button"
              >
                + Add Variant
              </button>
              {form.variants.map((v: Variant, i: number) => (
                <div
                  className="space-y-2 rounded border border-gray-300 p-3 dark:border-gray-700"
                  key={i}
                >
                  <input
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    onChange={(e) => updateVariant(i, "name", e.target.value)}
                    placeholder="Variant Name (Small, Medium)"
                    value={v.name}
                  />
                  <input
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    onChange={(e) => updateVariant(i, "price", e.target.value)}
                    placeholder="Price"
                    type="number"
                    value={v.price}
                  />
                  <input
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    onChange={(e) => updateVariant(i, "stock", e.target.value)}
                    placeholder="Stock"
                    type="number"
                    value={v.stock}
                  />
                  <button
                    className="text-red-600 dark:text-red-400"
                    onClick={() => removeVariant(i)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* STEP 4 — ADDONS */}
          {step === 4 && (
            <div className="space-y-3">
              <button
                className="rounded bg-blue-600 px-3 py-2 text-white dark:bg-blue-700"
                onClick={addAddon}
                type="button"
              >
                + Add Add-on
              </button>
              {form.addons.map((a: Addon, i: number) => (
                <div
                  className="space-y-2 rounded border border-gray-300 p-3 dark:border-gray-700"
                  key={i}
                >
                  <input
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    onChange={(e) => updateAddon(i, "name", e.target.value)}
                    placeholder="Add-on Name"
                    value={a.name}
                  />
                  <input
                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    onChange={(e) => updateAddon(i, "price", e.target.value)}
                    placeholder="Add-on Price"
                    type="number"
                    value={a.price}
                  />
                  <button
                    className="text-red-600 dark:text-red-400"
                    onClick={() => removeAddon(i)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* STEP 5 — IMAGES */}
          {step === 5 && (
            <div className="space-y-3">
              {/* Hide file upload on edit, only show preview */}
              <div className="grid grid-cols-3 gap-2">
                {form.images.map((img: string, i: number) => (
                  <img
                    alt="preview"
                    className="h-24 w-full rounded border border-gray-300 object-cover dark:border-gray-700"
                    key={i}
                    src={getImageUrl(img)}
                  />
                ))}
              </div>
            </div>
          )}
          {/* STEP 6 — REVIEW */}
          {step === 6 && (
            <div className="space-y-2 text-gray-800 dark:text-gray-200">
              <p>
                <b>Name:</b> {form.name}
              </p>
              <p>
                <b>Category:</b> {form.category}
              </p>
              <p>
                <b>Price:</b> R{form.price}
              </p>
              <p>
                <b>Variants:</b> {form.variants.length}
              </p>
              <p>
                <b>Add-ons:</b> {form.addons.length}
              </p>
              <p>
                <b>Images:</b> {form.images.length}
              </p>
            </div>
          )}
          {/* NAVIGATION */}
          <div className="flex justify-between pt-4">
            <button
              className="rounded bg-gray-400 px-4 py-2 text-white disabled:opacity-50 dark:bg-gray-700"
              disabled={step === 1}
              onClick={() => setStep(step - 1)}
              type="button"
            >
              Back
            </button>
            {step < 6 ? (
              <button
                className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-700"
                disabled={!isStepValid()}
                onClick={() => setStep(step + 1)}
                type="button"
              >
                Next
              </button>
            ) : (
              <button
                className="rounded bg-green-600 px-4 py-2 text-white dark:bg-green-700"
                onClick={submit}
                type="button"
              >
                Save
              </button>
            )}
          </div>
        </div>
        {/* --- RIGHT SIDE LIVE PREVIEW --- */}
        <div className="hidden md:block">
          <h3 className="mb-3 text-lg font-bold text-gray-900 dark:text-gray-100">
            Live Preview
          </h3>
          <div className="space-y-3 rounded-lg border border-gray-300 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            {form.images[0] ? (
              <img
                className="h-40 w-full rounded border border-gray-300 object-cover dark:border-gray-700"
                src={getImageUrl(form.images[0])}
              />
            ) : (
              <div className="flex h-40 w-full items-center justify-center rounded bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
                No Image
              </div>
            )}
            <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {form.name || "Item Name"}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {form.description || "Description..."}
            </p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              R{form.price || "0.00"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemEditModal;
